import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth, usePermissions } from './lib/auth-context';
import { PERMISSIONS, type Permission } from './lib/permissions';
import { fetchMyCourses } from './lib/api';
import { resolveLanding, COURSE_SLUGS } from './lib/resolve-landing';
import { ThemeProvider } from './lib/theme-context';
import { useReferralCapture } from './lib/use-referral-capture';
import ErrorBoundary from './components/ErrorBoundary';
import OnboardingWizard from './components/OnboardingWizard';
import AdminLayout from './components/AdminLayout';
import CampusShell from './components/CampusShell';
import AutoShell from './components/AutoShell';
import Dashboard from './pages/Dashboard';

// Code-split: las rutas menos frecuentes se cargan on-demand.
// - Login / AuthCallback solo cuando el usuario no esta autenticado.
// - ModuleDetail solo al abrir un modulo concreto.
// - NotFound practicamente nunca.
// Landing + Dashboard quedan sync (entry + home post-login).
const Login = lazy(() => import('./pages/Login'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const ModuleDetail = lazy(() => import('./pages/ModuleDetail'));
// Índice "Mis cursos" (item de menú "Cursos" · 2026-06-17). Antes "Cursos"
// llevaba fijo a /curso/robotea-start.
const Cursos = lazy(() => import('./pages/Cursos'));
const NotFound = lazy(() => import('./pages/NotFound'));
// /my-plan (renombrado desde /mi-plan el 2026-05-30 · regla URLs en inglés).
// /mi-plan se redirige abajo para no romper bookmarks viejos.
const MyPlan = lazy(() => import('./pages/MyPlan'));
// /my-robots (renombrado desde /mis-robots el 2026-05-30 · regla global de
// URLs en inglés para internacionalización). /mis-robots y /mis-robots/activar
// se redirigen abajo en las rutas para no romper bookmarks antiguos.
const MyRobots = lazy(() => import('./pages/MyRobots'));
const MyRobotsActivate = lazy(() => import('./pages/MyRobotsActivate'));
const Comunidad = lazy(() => import('./pages/Comunidad'));
// Paginas legales · code-split (visita esporadica · contenido denso)
const Privacidad = lazy(() => import('./pages/Privacidad'));
const Terminos = lazy(() => import('./pages/Terminos'));
const AvisoLegal = lazy(() => import('./pages/AvisoLegal'));
const Contacto = lazy(() => import('./pages/Contacto'));
const Perfil = lazy(() => import('./pages/Perfil'));
// Progreso · gamificación (XP/Nivel/Rango + recompensas) separada del perfil.
const Progreso = lazy(() => import('./pages/Progreso'));
const Billing = lazy(() => import('./pages/Billing'));
const Certificado = lazy(() => import('./pages/Certificado'));

const Notificaciones = lazy(() => import('./pages/Notificaciones'));
const Admin = lazy(() => import('./pages/Admin'));
const AdminUsuarios = lazy(() => import('./pages/AdminUsuarios'));
const AdminLicencias = lazy(() => import('./pages/AdminLicencias'));
const AdminQuizzes = lazy(() => import('./pages/AdminQuizzes'));
const AdminEvents = lazy(() => import('./pages/AdminEvents'));
const AdminContenido = lazy(() => import('./pages/AdminContenido'));
const AdminActividad = lazy(() => import('./pages/AdminActividad'));
const AdminAnalitica = lazy(() => import('./pages/AdminAnalitica'));
const AdminIntegraciones = lazy(() => import('./pages/AdminIntegraciones'));
const AdminUsuario360 = lazy(() => import('./pages/AdminUsuario360'));
const AdminSugerencias = lazy(() => import('./pages/AdminSugerencias'));
const AdminWaitlist = lazy(() => import('./pages/AdminWaitlist'));
const AdminEnlaces = lazy(() => import('./pages/AdminEnlaces'));
const Buzon = lazy(() => import('./pages/Buzon'));
const Guia = lazy(() => import('./pages/Guia'));
const Referidos = lazy(() => import('./pages/Referidos'));
const DashboardPreview = lazy(() => import('./pages/DashboardPreview'));
// Multi-curso · sprint 2026-05-27
// Catalogo eliminado 2026-05-30 · todo el portfolio (cursos + servicios + Club)
// vive en Inicio. /catalogo redirige a /dashboard para no romper bookmarks viejos.
const Curso = lazy(() => import('./pages/Curso'));
const CourseBonusesList = lazy(() => import('./pages/CourseBonusesList'));
const CourseBonus = lazy(() => import('./pages/CourseBonus'));
// Vista previa de diseño · Bonos Robotea Start · 2026-05-31 → 2026-06-02
// Se eliminan cuando el diseño se migre al CourseBonus real con BD.
const PreviewBono = lazy(() => import('./pages/PreviewBono'));
const PreviewBonoPrimerEncendido = lazy(() => import('./pages/PreviewBonoPrimerEncendido'));
const PreviewBonoCalculadora = lazy(() => import('./pages/PreviewBonoCalculadora'));
const PreviewBonoAntiSabotaje = lazy(() => import('./pages/PreviewBonoAntiSabotaje'));
const PreviewBonoAlertaRoja = lazy(() => import('./pages/PreviewBonoAlertaRoja'));
// Sprint tienda-robot-eas · 2026-05-28
const TiendaEAs = lazy(() => import('./pages/TiendaEAs'));
// Marketdata · Señales (Fase 1 · admin-only · feat/campus-marketdata) · 2026-06-06
const Senales = lazy(() => import('./pages/Senales'));
const RoboteaLab = lazy(() => import('./pages/RoboteaLab'));
const LeccionMonkeyTest = lazy(() => import('./pages/LeccionMonkeyTest'));
// Marketdata · Mercado (Fase 2 · Basic/Pro · briefing diario) · 2026-06-06
const Mercado = lazy(() => import('./pages/Mercado'));
// RoboTeam · área de miembros gated por entitlement (Basic/Pro) · 2026-06-25.
// SCAFFOLD: el gate de entitlement vive dentro de la propia página (RoboTeam.tsx
// resuelve el plan desde fetchMyCourses y muestra Pro/Basic/CTA).
const RoboTeam = lazy(() => import('./pages/RoboTeam'));
// Área de miembros nueva de Roboteam (fase 1, solo staging). Ruta aparte del campus actual.
const MiembrosShell = lazy(() => import('./pages/miembros/MiembrosShell'));
const MiembrosInicio = lazy(() => import('./pages/miembros/MiembrosInicio'));
const MiembrosAprender = lazy(() => import('./pages/miembros/MiembrosAprender'));
const MiembrosLeccion = lazy(() => import('./pages/miembros/MiembrosLeccion'));
const MiembrosRobots = lazy(() => import('./pages/miembros/MiembrosRobots'));
const MiembrosMas = lazy(() => import('./pages/miembros/MiembrosMas'));
// Robotea Auto · venta cruzada in-campus (/auto/activar) · 2026-07-08.
// El gate por entitlement vive en la propia página: con robotea-auto
// redirige a /auto; sin él muestra el pitch premium + CTA checkout GHL.
const AutoActivar = lazy(() => import('./pages/AutoActivar'));
// Robotea Auto · home del panel de miembros (/auto) · 2026-07-08.
// Vive bajo AutoShell (layout-route HERMANA de CampusShell, chrome propio).
const AutoHome = lazy(() => import('./pages/auto/AutoHome'));
import RegistroDemo from './pages/RegistroDemo';

// Gate de autenticación · defensa en profundidad para TODAS las rutas
// no públicas del campus (defaul-deny). Mientras el nuevo campus está en
// pruebas (solo Cristian + admins), evita filtraciones por páginas que
// no llevan su propio Navigate to /login a nivel de componente.
// Whitelist pública: /, /login, /auth/callback, /privacidad, /terminos,
// /aviso-legal, /contacto, /404 (y los Navigate redirects).
function RequireAuth({ children }: { children: JSX.Element }): JSX.Element {
  const { state } = useAuth();
  if (state.status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center text-ink/50">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-ink/15 border-t-brand-violet" />
      </div>
    );
  }
  if (state.status === 'unauthenticated') {
    // Recuerda a dónde iba (p. ej. /miembros) para volver tras el login.
    try {
      sessionStorage.setItem('rb_return_to', window.location.pathname + window.location.search);
    } catch {
      /* sin storage: el login cae a /dashboard */
    }
    return <Navigate to="/login" replace />;
  }
  return children;
}

// Raíz `/` (campus.robotea.es / app.robotea.io): SIN landing (decisión Jaime
// 2026-06-17). Va directo a login si no hay sesión, o al destino que resuelve
// `resolveLanding` según el entitlement (por defecto /dashboard) si la hay.
//
// RETROCOMPATIBILIDAD: mientras nadie tenga el entitlement 'robotea-auto',
// resolveLanding devuelve /dashboard para todos, exactamente como antes. La
// única bifurcación nueva es Auto-solo → /auto. Cargamos los cursos del alumno
// (fetchMyCourses, mismo patrón que RoboTeam) y MOSTRAMOS EL SPINNER mientras
// se resuelven, para no redirigir prematuramente y provocar un doble salto.
export function RootRedirect(): JSX.Element {
  const { state } = useAuth();
  const auth = state.status === 'authenticated' ? state.identity : null;
  // null = aún cargando; string[] = slugs resueltos (o [] si el fetch falló).
  const [enrolledSlugs, setEnrolledSlugs] = useState<string[] | null>(null);

  useEffect(() => {
    if (!auth) {
      setEnrolledSlugs(null);
      return;
    }
    let active = true;
    fetchMyCourses(auth)
      .then((mine) => {
        if (active) setEnrolledSlugs(mine.enrolled.map((c) => c.slug));
      })
      .catch(() => {
        // Fail-safe: nunca dejar al usuario colgado. Slugs vacíos →
        // resolveLanding devuelve /dashboard (comportamiento histórico).
        if (active) setEnrolledSlugs([]);
      });
    return () => {
      active = false;
    };
  }, [auth]);

  const spinner = (
    <div className="flex min-h-screen items-center justify-center text-ink/50">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-ink/15 border-t-brand-violet" />
    </div>
  );

  if (state.status === 'loading') return spinner;
  if (!auth) return <Navigate to="/login" replace />;
  // Autenticado pero los slugs aún no han cargado → spinner (no doble salto).
  if (enrolledSlugs === null) return spinner;

  const target = resolveLanding({
    authenticated: true,
    enrolledSlugs,
    courseSlugs: COURSE_SLUGS,
  });
  return <Navigate to={target} replace />;
}

// Gate por PERMISO para páginas que no usan AdminLayout (ej. /signals). Análogo
// a RequireAuth pero además exige un permiso concreto (F3 RBAC · espejo del
// require_permission del backend). Reemplaza al antiguo gate role === 'admin'.
function RequirePermission({
  permission,
  children,
}: {
  permission: Permission;
  children: JSX.Element;
}): JSX.Element {
  const { state } = useAuth();
  const { can } = usePermissions();
  if (state.status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center text-ink/50">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-ink/15 border-t-brand-violet" />
      </div>
    );
  }
  if (state.status === 'unauthenticated') {
    return <Navigate to="/login" replace />;
  }
  if (!can(permission)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

// Captura del referido por URL (?ref=CODE) + atributo post-login. Vive dentro
// de AuthProvider porque el hook necesita useAuth(). No renderiza nada.
function ReferralCapture(): null {
  useReferralCapture();
  return null;
}

function RouteFallback(): JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-[60vh] items-center justify-center px-6 py-12 text-center"
    >
      <div>
        <div
          aria-hidden="true"
          className="mx-auto mb-3 h-7 w-7 animate-spin rounded-full border-2 border-ink/15 border-t-brand-violet"
        />
        <p className="text-sm text-ink/50">Cargando…</p>
      </div>
    </div>
  );
}

export default function App(): JSX.Element {
  return (
    <ErrorBoundary>
      <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <ReferralCapture />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* ── Rutas públicas · SIN shell persistente ──────────────────
                  Landing/Login traen su propio marco (showHeader=false); las
                  páginas legales se renderizan con el footer público de Layout
                  pero sin sidebar (no hay sesión). Quedan fuera de CampusShell. */}
              <Route path="/" element={<RootRedirect />} />
              <Route path="/login" element={<Login />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/privacidad" element={<Privacidad />} />
              <Route path="/terminos" element={<Terminos />} />
              <Route path="/aviso-legal" element={<AvisoLegal />} />
              <Route path="/contacto" element={<Contacto />} />
              <Route path="/registro" element={<RegistroDemo />} />
              <Route path="/404" element={<NotFound />} />

              {/* Redirects legacy · solo navegan, no renderizan contenido. */}
              <Route path="/catalogo" element={<Navigate to="/dashboard" replace />} />
              <Route path="/mis-robots" element={<Navigate to="/my-robots" replace />} />
              <Route path="/mis-robots/activar" element={<Navigate to="/my-robots/activate" replace />} />
              <Route path="/mi-plan" element={<Navigate to="/my-plan" replace />} />
              <Route path="/senales" element={<Navigate to="/signals" replace />} />
              <Route path="/mercado" element={<Navigate to="/market" replace />} />

              {/* ── Rutas autenticadas · DENTRO del shell PERSISTENTE ───────
                  CampusShell monta el AppShell (sidebar + barra superior) UNA
                  vez y renderiza la página activa en su <Outlet/>. Al navegar
                  entre estas rutas el chrome PERSISTE (no se desmonta) → sin
                  parpadeo. Cada página sigue envolviéndose en <Layout>, que en
                  este contexto rinde solo contenido + pie legal. */}
              <Route element={<CampusShell />}>
                <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
                <Route path="/dashboard/modulo/:slug" element={<RequireAuth><ModuleDetail /></RequireAuth>} />
                <Route path="/tienda-eas" element={<RequireAuth><TiendaEAs /></RequireAuth>} />
                <Route path="/cursos" element={<RequireAuth><Cursos /></RequireAuth>} />
                <Route path="/curso/:slug" element={<RequireAuth><Curso /></RequireAuth>} />
                <Route path="/curso/:slug/bonos" element={<RequireAuth><CourseBonusesList /></RequireAuth>} />
                <Route path="/curso/:slug/bono/:bonusSlug" element={<RequireAuth><CourseBonus /></RequireAuth>} />
                <Route path="/my-robots" element={<RequireAuth><MyRobots /></RequireAuth>} />
                <Route path="/my-robots/activate" element={<RequireAuth><MyRobotsActivate /></RequireAuth>} />
                <Route path="/comunidad" element={<RequireAuth><Comunidad /></RequireAuth>} />
                <Route path="/my-plan" element={<RequireAuth><MyPlan /></RequireAuth>} />
                <Route path="/admin" element={<AdminLayout requiredPermission={PERMISSIONS.usuariosView}><Admin /></AdminLayout>} />
                {/* Apartado "Usuarios" · dos vistas (alumnos vs staff). La ruta
                    legacy /admin/usuarios redirige a /admin/usuarios/alumnos
                    para no romper bookmarks. Las rutas específicas (alumnos /
                    staff) PRECEDEN a la paramétrica :userId para que no las
                    capture como id. */}
                <Route path="/admin/usuarios" element={<Navigate to="/admin/usuarios/alumnos" replace />} />
                <Route path="/admin/usuarios/alumnos" element={<AdminLayout requiredPermission={PERMISSIONS.usuariosView}><AdminUsuarios mode="students" /></AdminLayout>} />
                <Route path="/admin/usuarios/staff" element={<AdminLayout requiredPermission={PERMISSIONS.usuariosView}><AdminUsuarios mode="staff" /></AdminLayout>} />
                <Route path="/admin/usuarios/:userId" element={<AdminLayout requiredPermission={PERMISSIONS.usuariosView}><AdminUsuario360 /></AdminLayout>} />
                <Route path="/admin/actividad" element={<AdminLayout requiredPermission={PERMISSIONS.actividadView}><AdminActividad /></AdminLayout>} />
                <Route path="/admin/analitica" element={<AdminLayout requiredPermission={PERMISSIONS.analiticaView}><AdminAnalitica /></AdminLayout>} />
                <Route path="/admin/integraciones" element={<AdminLayout requiredPermission={PERMISSIONS.integraciones}><AdminIntegraciones /></AdminLayout>} />
                <Route path="/admin/enlaces" element={<AdminLayout requiredPermission={PERMISSIONS.integraciones}><AdminEnlaces /></AdminLayout>} />
                <Route path="/admin/contenido" element={<AdminLayout requiredPermission={PERMISSIONS.contenidoWrite}><AdminContenido /></AdminLayout>} />
                <Route path="/admin/quizzes" element={<AdminLayout requiredPermission={PERMISSIONS.contenidoWrite}><AdminQuizzes /></AdminLayout>} />
                <Route path="/admin/eventos" element={<AdminLayout requiredPermission={PERMISSIONS.contenidoWrite}><AdminEvents /></AdminLayout>} />
                <Route path="/admin/licencias" element={<AdminLayout requiredPermission={PERMISSIONS.licenciasView}><AdminLicencias /></AdminLayout>} />
                <Route path="/admin/sugerencias" element={<AdminLayout requiredPermission={PERMISSIONS.sugerenciasView}><AdminSugerencias /></AdminLayout>} />
                <Route path="/admin/waitlist" element={<AdminLayout requiredPermission={PERMISSIONS.usuariosView}><AdminWaitlist /></AdminLayout>} />
                {/* /signals (renombrado desde /senales · regla URLs en inglés
                    para i18n, análogo a /market). La etiqueta visible sigue
                    siendo "Señales". */}
                <Route path="/signals" element={<RequirePermission permission={PERMISSIONS.analiticaView}><Senales /></RequirePermission>} />
                <Route path="/robotea-lab" element={<RequirePermission permission={PERMISSIONS.roboteaLabView}><RoboteaLab /></RequirePermission>} />
                {/* Vista previa de lección — NO es Robotea Lab (Jaime 2026-08-30: "las de
                    lecciones no tienen por qué ir en Robotea Lab"). Vive dentro del curso,
                    mismo patrón que /curso/:slug/bonos — Jaime pidió explícitamente
                    /curso/robotea-basic/test-lecciones. */}
                <Route path="/curso/:slug/test-lecciones" element={<RequirePermission permission={PERMISSIONS.roboteaLabView}><LeccionMonkeyTest /></RequirePermission>} />
                {/* /market (renombrado desde /mercado · regla URLs en inglés
                    para i18n). La etiqueta visible sigue siendo "Mercado". */}
                <Route path="/market" element={<RequireAuth><Mercado /></RequireAuth>} />
                {/* /roboteam · área de miembros RoboTeam (Basic/Pro). El gate de
                    autenticación es RequireAuth; el gate por ENTITLEMENT (qué plan
                    ve o si ve un CTA) lo resuelve la propia página desde los
                    course_slug matriculados. SCAFFOLD 2026-06-25. */}
                <Route path="/roboteam" element={<RequireAuth><RoboTeam /></RequireAuth>} />
                {/* /auto/activar · venta cruzada Robotea Auto (premium). El gate
                    por entitlement lo resuelve la página: con robotea-auto
                    redirige a /auto; sin él muestra el pitch + CTA checkout. */}
                <Route path="/auto/activar" element={<RequireAuth><AutoActivar /></RequireAuth>} />
                <Route path="/referidos" element={<RequireAuth><Referidos /></RequireAuth>} />
                <Route path="/perfil" element={<RequireAuth><Perfil /></RequireAuth>} />
                <Route path="/buzon" element={<RequireAuth><Buzon /></RequireAuth>} />
                <Route path="/guia" element={<RequireAuth><Guia /></RequireAuth>} />
                <Route path="/progreso" element={<RequireAuth><Progreso /></RequireAuth>} />
                <Route path="/notificaciones" element={<RequireAuth><Notificaciones /></RequireAuth>} />
                <Route path="/billing" element={<RequireAuth><Billing /></RequireAuth>} />
                <Route path="/preview/dashboard" element={<RequireAuth><DashboardPreview /></RequireAuth>} />
                <Route path="/preview/bono/mapa-arranque-2h" element={<RequireAuth><PreviewBono /></RequireAuth>} />
                <Route path="/preview/bono/primer-encendido" element={<RequireAuth><PreviewBonoPrimerEncendido /></RequireAuth>} />
                <Route path="/preview/bono/calculadora-primer-capital" element={<RequireAuth><PreviewBonoCalculadora /></RequireAuth>} />
                <Route path="/preview/bono/anti-sabotaje" element={<RequireAuth><PreviewBonoAntiSabotaje /></RequireAuth>} />
                <Route path="/preview/bono/alerta-roja" element={<RequireAuth><PreviewBonoAlertaRoja /></RequireAuth>} />
                <Route path="/certificado" element={<RequireAuth><Certificado /></RequireAuth>} />
              </Route>

              {/* ── Robotea Auto · área de miembros PREMIUM (/auto/*) ───────
                  Layout-route HERMANA de CampusShell (NO anidada): Auto es un
                  producto aparte con chrome y nav propios (AutoShell). El gate
                  por entitlement es FAIL-CLOSED dentro de AutoShell: sin
                  'robotea-auto' → Navigate a /auto/activar (la venta, que sí
                  vive en el campus). Solo /auto en este spec; el resto de
                  secciones (cuenta/robots/informes/…) llega con el Panel Auto. */}
              <Route element={<AutoShell />}>
                <Route path="/auto" element={<RequireAuth><AutoHome /></RequireAuth>} />
              </Route>

              {/* Campus nuevo de Roboteam (propuesta visual, solo staging): marco propio, sin Layout. */}
              <Route path="/miembros" element={<RequireAuth><MiembrosShell /></RequireAuth>}>
                <Route index element={<MiembrosInicio />} />
                <Route path="aprender" element={<MiembrosAprender />} />
                <Route path="leccion/:module/:position" element={<MiembrosLeccion />} />
                <Route path="robots" element={<MiembrosRobots />} />
                <Route path="mas" element={<MiembrosMas />} />
              </Route>

              <Route path="*" element={<Navigate to="/404" replace />} />
            </Routes>
          </Suspense>
          {/* OnboardingWizard vive dentro de AuthProvider · necesita useAuth().
              Server-driven via /api/me/profile.onboarding_* (Wave 2). */}
          <OnboardingWizard />
        </AuthProvider>
      </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
